import React, { useMemo } from "react";
import CallQualityDistributionWidget, {
  type CallQualityDistributionData,
} from "./CallQualityDistributionWidget";
import CallQualityMetricCard from "./CallQualityMetricCard";
import type { MetricValueStatus } from "./CallQualityMetricCard";
import { CARD_TOOLTIPS } from "./constants/callQualityCardTooltips";
import type { CallQualityScorecardResponse } from "@/lib/axios/api/call-quality.api";

export interface CallQualityMetricsSectionData {
  distribution?: CallQualityDistributionData | null;
  totalCallsOffered?: number;
  totalCallsOfferedTrend?: string;
  poorCallRate?: string;
  callSetUpFailed?: string;
  callDropFailed?: string;
  packetLoss?: string;
  avgMosScore?: string;
  avgJitter?: string;
  roundTrip?: string;
}

function getCardFromPayload(
  payload: CallQualityScorecardResponse,
  metricName: string
): { value: string; target: string | null; trend: string | undefined; valueStatus: MetricValueStatus } | null {
  const card = payload.kpiCards.find((c) => c.metricName === metricName);
  if (!card) return null;
  const trend =
    card.percentageChange != null
      ? `${card.percentageChange >= 0 ? "+" : ""}${card.percentageChange.toFixed(2)}%`
      : undefined;
  const valueStatus: MetricValueStatus = card.isTargetMet ? "good" : "warning";
  return { value: card.value, target: card.target, trend, valueStatus };
}

interface CallQualityMetricsSectionProps {
  /** Legacy flat data (fallback when no API payload) */
  data?: CallQualityMetricsSectionData | null;
  /** Response from Get Call Quality Scorecard API */
  scorecardPayload?: CallQualityScorecardResponse | null;
  loading?: boolean;
  onSetupFailedClick?: (callMicrosoftIds: string[]) => void;
  onDropFailedClick?: (callMicrosoftIds: string[]) => void;
  onQualityDistributionClick?: (quality: "Good" | "Poor" | "Unclassified") => void;
}

const CallQualityMetricsSection: React.FC<CallQualityMetricsSectionProps> = ({
  data,
  scorecardPayload,
  loading,
  onSetupFailedClick,
  onDropFailedClick,
  onQualityDistributionClick,
}) => {
  const distributionForDonut = useMemo((): CallQualityDistributionData | undefined => {
    if (scorecardPayload?.distribution) {
      const d = scorecardPayload.distribution;
      return {
        good: d.goodPercentage,
        poor: d.poorPercentage,
        unclassified: d.unclassifiedPercentage,
      };
    }
    return data?.distribution ?? undefined;
  }, [scorecardPayload, data?.distribution]);

  const useApi = Boolean(scorecardPayload?.kpiCards?.length);

  const totalCallsCard = useApi ? getCardFromPayload(scorecardPayload!, "Total Calls Offered") : null;
  const poorCallRateCard = useApi ? getCardFromPayload(scorecardPayload!, "Poor Call Rate") : null;
  const callSetUpFailedCard = useApi ? getCardFromPayload(scorecardPayload!, "Call Set Up Failed") : null;
  const callDropFailedCard = useApi ? getCardFromPayload(scorecardPayload!, "Call Drop Failed") : null;
  const packetLossCard = useApi ? getCardFromPayload(scorecardPayload!, "Packet Loss") : null;
  const avgMosScoreCard = useApi ? getCardFromPayload(scorecardPayload!, "Avg MOS Score") : null;
  const avgJitterCard = useApi ? getCardFromPayload(scorecardPayload!, "Avg Jitter") : null;
  const roundTripCard = useApi ? getCardFromPayload(scorecardPayload!, "Round Trip") : null;
  const setupFailedCallIds = scorecardPayload?.failureCallIds?.setupFailedCallIds ?? scorecardPayload?.callSetupIds ?? [];
  const dropFailedCallIds = scorecardPayload?.failureCallIds?.dropFailedCallIds ?? scorecardPayload?.callDropIds ?? [];
  const canClickSetupFailed = setupFailedCallIds.length > 0 && typeof onSetupFailedClick === "function";
  const canClickDropFailed = dropFailedCallIds.length > 0 && typeof onDropFailedClick === "function";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[400px_1fr] gap-3 mb-6">
      <div className="lg:w-[400px] h-[236px] flex flex-col lg:flex-none">
        <CallQualityDistributionWidget
          data={distributionForDonut}
          loading={loading}
          onQualityClick={onQualityDistributionClick}
        />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 min-w-0">
        <CallQualityMetricCard
          title="Total Calls Offered"
          iconTooltip={CARD_TOOLTIPS["Total Calls Offered"]}
          value={totalCallsCard?.value ?? (data?.totalCallsOffered != null ? data.totalCallsOffered.toLocaleString() : "31,800")}
          valueStatus={totalCallsCard?.valueStatus ?? "neutral"}
          trend={totalCallsCard?.trend ?? data?.totalCallsOfferedTrend}
          loading={loading}
        />
        <CallQualityMetricCard
          title="Poor Call Rate"
          iconTooltip={CARD_TOOLTIPS["Poor Call Rate"]}
          value={poorCallRateCard?.value ?? data?.poorCallRate ?? "3.2%"}
          target={poorCallRateCard?.target ?? "≤2%"}
          loading={loading}
        />
        <CallQualityMetricCard
          title="Set Up Failure Rate"
          iconTooltip={CARD_TOOLTIPS["Call Set Up Failed"]}
          value={callSetUpFailedCard?.value ?? data?.callSetUpFailed ?? "2.4%"}
          target={callSetUpFailedCard?.target ?? "≤2%"}
          loading={loading}
          onClick={canClickSetupFailed ? () => onSetupFailedClick?.(setupFailedCallIds) : undefined}
        />
        <CallQualityMetricCard
          title="Drop Failure Rate"
          iconTooltip={CARD_TOOLTIPS["Call Drop Failed"]}
          value={callDropFailedCard?.value ?? data?.callDropFailed ?? "1.8%"}
          target={callDropFailedCard?.target ?? "≤2%"}
          loading={loading}
          onClick={canClickDropFailed ? () => onDropFailedClick?.(dropFailedCallIds) : undefined}
        />
        <CallQualityMetricCard
          title="Avg Packet Loss"
          iconTooltip={CARD_TOOLTIPS["Avg Packet Loss"]}
          value={packetLossCard?.value ?? data?.packetLoss ?? "0.4%"}
          target={packetLossCard?.target ?? "≤1%"}
          loading={loading}
        />
        <CallQualityMetricCard
          title="Avg MOS Score"
          iconTooltip={CARD_TOOLTIPS["Avg MOS Score"]}
          value={avgMosScoreCard?.value ?? data?.avgMosScore ?? "4.1"}
          target={avgMosScoreCard?.target ?? "≥4.0"}
          loading={loading}
        />
        <CallQualityMetricCard
          title="Avg Jitter"
          iconTooltip={CARD_TOOLTIPS["Avg Jitter"]}
          value={avgJitterCard?.value ?? data?.avgJitter ?? "8.5ms"}
          target={avgJitterCard?.target ?? "≤30ms"}
          loading={loading}
        />
        <CallQualityMetricCard
          title="Avg Round Trip"
          iconTooltip={CARD_TOOLTIPS["Avg Round Trip"]}
          value={roundTripCard?.value ?? data?.roundTrip ?? "45ms"}
          target={roundTripCard?.target ?? "≤100ms"}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default CallQualityMetricsSection;
